import Card, { CardContent } from '@/components/card';
import { ExpandMoreButton } from '@/components/expand-more-button';
import TextStack, {
  TextLabel,
  TextStackItem,
  TextValue
} from '@/components/text-stack';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { DisbursementRequestAlertDto } from '@/models/suba/alerts/disbursement-request-alert-dto.model';
import { DisbursementUpdateNoteRequest } from '@/models/suba/disbursements/DisbursementUpdateNoteRequest.model';
import { FeatureLevelPermissions } from '@/models/UserPermissions.model';
import { useAlertContext } from '@/routes/suba/common/contexts/AlertContext';
import { DisbursementService } from '@/services/suba/disbursement/Disbursement.service';
import { userService } from '@/services/User.service';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToggle } from '@vestwell-frontend/hooks';

import { Field, Form, Formik } from 'formik';
import { FC } from 'react';
import * as yup from 'yup';

import { CardHeader } from '../../../common/CardHeader.component';

const editNotesSchema = yup.object().shape({
  jiraTicketId: yup
    .string()
    .test('is-jira-ticket-id', 'Invalid JIRA Ticket ID', value => {
      if (!value) return true;
      const regex = /^((?<!([A-Z]{1,10})-?)[A-Z]+-\d+)$/;
      return value.length <= 50 && regex.test(value);
    }),
  notes: yup.string()
});

export const DisbursementNotes: FC = () => {
  const alert = useAlertContext<DisbursementRequestAlertDto>();
  const { showSnackbar } = useSnackbar();
  const [isExpanded, toggleIsExpanded] = useToggle(true);
  const queryClient = useQueryClient();

  const getDisbursementQuery = useQuery(
    ['DisbursementService.getById', alert.details?.disbursementRequestQueueId],
    () => DisbursementService.getById(alert.details.disbursementRequestQueueId),
    {
      enabled: !!alert.details?.disbursementRequestQueueId
    }
  );

  const editNotesMutation = useMutation({
    mutationFn: (params: DisbursementUpdateNoteRequest) =>
      DisbursementService.updateNote(
        alert.details.disbursementRequestQueueId,
        params
      ),
    mutationKey: [
      'DisbursementService.updateNote',
      alert.details.disbursementRequestQueueId
    ],
    onSettled: () => {
      toggleIsEditing(false);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([
        'DisbursementService.getById',
        alert.details.disbursementRequestQueueId
      ]);

      showSnackbar({
        message: 'Disbursement Request updated successfully',
        severity: 'success'
      });
    }
  });

  const [isEditing, toggleIsEditing] = useToggle(false);

  return (
    <Card size='small'>
      <CardHeader
        action={
          <ExpandMoreButton
            aria-label='expand notes'
            data-testid='notes-expand-more-button'
            expand={isExpanded}
            onClick={toggleIsExpanded}
          />
        }
        disableTypography
        title={
          <Stack
            alignItems='center'
            direction='row'
            overflow='hidden'
            spacing={2}>
            <Typography variant='h6'>Notes</Typography>
            {!isExpanded && (
              <Stack direction='row' spacing={1}>
                {getDisbursementQuery.data?.ticketId && (
                  <Typography>{getDisbursementQuery.data.ticketId}</Typography>
                )}
                {getDisbursementQuery.data?.ticketId &&
                  getDisbursementQuery.data?.reason && (
                    <Divider flexItem orientation='vertical' />
                  )}
                {getDisbursementQuery.data?.reason && (
                  <Typography maxWidth={250} noWrap>
                    {getDisbursementQuery.data.reason}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        }
      />
      <Divider />
      <Collapse in={isExpanded}>
        <CardContent>
          {isEditing ? (
            <Formik
              enableReinitialize
              initialValues={{
                jiraTicketId: getDisbursementQuery.data?.ticketId || '',
                notes: getDisbursementQuery.data?.reason || ''
              }}
              onSubmit={values =>
                editNotesMutation.mutate({
                  reason: values.notes,
                  ticketId: values.jiraTicketId
                })
              }
              validationSchema={editNotesSchema}>
              {({ errors, touched }) => (
                <Form data-testid='edit-notes-form'>
                  <Stack spacing={2}>
                    <FormControl fullWidth size='small'>
                      <InputLabel htmlFor='jira-ticket-id-input'>
                        JIRA Ticket ID
                      </InputLabel>
                      <Field
                        as={OutlinedInput}
                        autoFocus
                        data-testid='jira-ticket-id-input'
                        error={touched.jiraTicketId && !!errors.jiraTicketId}
                        helperText={touched.jiraTicketId && errors.jiraTicketId}
                        id='jira-ticket-id-input'
                        label='JIRA Ticket ID'
                        name='jiraTicketId'
                      />
                      <FormHelperText>{errors.jiraTicketId}</FormHelperText>
                    </FormControl>
                    <FormControl fullWidth size='small'>
                      <InputLabel htmlFor='notes-input'>Notes</InputLabel>
                      <Field
                        as={OutlinedInput}
                        data-testid='notes-input'
                        error={touched.notes && !!errors.notes}
                        helperText={touched.notes && errors.notes}
                        id='notes-input'
                        label='Notes'
                        multiline
                        name='notes'
                      />
                    </FormControl>
                    <FormHelperText>{errors.notes}</FormHelperText>
                    <Stack direction='row' spacing={1}>
                      <LoadingButton
                        data-testid='edit-notes-save-button'
                        disabled={
                          !userService.hasPermission(
                            FeatureLevelPermissions.WRITE_SUBA_ALERTS
                          ) ||
                          !!errors.jiraTicketId ||
                          !!errors.notes
                        }
                        loading={editNotesMutation.isLoading}
                        type='submit'
                        variant='contained'>
                        Save
                      </LoadingButton>
                      <Button
                        data-testid='edit-notes-cancel-button'
                        onClick={toggleIsEditing}>
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Form>
              )}
            </Formik>
          ) : (
            <Stack direction='row' spacing={2}>
              <TextStack direction='column' flexGrow={1}>
                <TextStackItem>
                  <TextLabel>JIRA Ticket ID</TextLabel>
                  <TextValue>
                    {getDisbursementQuery.data?.ticketId || 'No JIRA Ticket ID'}
                  </TextValue>
                </TextStackItem>
                <TextStackItem>
                  <TextLabel>Reason</TextLabel>
                  <TextValue>
                    {getDisbursementQuery.data?.reason ||
                      'No notes for this withdrawal'}
                  </TextValue>
                </TextStackItem>
              </TextStack>
              <Button
                data-testid='edit-notes-button'
                disabled={
                  !userService.hasPermission(
                    FeatureLevelPermissions.WRITE_SUBA_ALERTS
                  )
                }
                onClick={toggleIsEditing}>
                Edit
              </Button>
            </Stack>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

DisbursementNotes.displayName = 'DisbursementDetails';
